'use client';
import { useState } from 'react';

import cn from 'classnames';

import { Locale } from '@core/Entities/Locale/Locale.entity';
import { Tick } from '@gds/Icons/Paths/Tick';
import { Minus } from '@gds/Icons/Paths/Minus';
import { Typography } from '@gds/Typography/Typography';

import { getFormattedCurrency } from '@core/Utils/Intl/GetFormattedCurrency';
import { isInstaMotion } from '@core/Utils/Vehicle/IsInstaMotion';

import { Vehicle } from '@core/Entities/Vehicle/Vehicle.entity';
import styles from './InstamotionFinance.module.css';
import { InstamotionFinanceMessages } from '../InstamotionFinanceWrapper/InstamotionFinance.messages';

type PurchaseTypes = 'finance' | 'purchase';

export const InstamotionFinance = ({
  locale,
  strings,
  vehicle,
}: {
  locale: Locale;
  strings: InstamotionFinanceMessages;
  vehicle: Vehicle;
}) => {
  const [selected, setSelected] = useState<PurchaseTypes>('finance');
  const hasInstaMotion = isInstaMotion(vehicle?.details?.dataSource);

  if (!hasInstaMotion || !vehicle.finance) {
    return null;
  }

  function handleClick(type: PurchaseTypes) {
    return () => {
      setSelected(type);
    };
  }

  const formatConfig = { minimumFractionDigits: 2, maximumFractionDigits: 2 };
  const formattedDeposit = getFormattedCurrency(vehicle.finance.deposit, locale, formatConfig);
  const formattedTotalPrice = getFormattedCurrency(vehicle.pricing.price, locale, formatConfig);
  const formattedMonthlyPrice = getFormattedCurrency(vehicle.finance.rate, locale, formatConfig);
  const formattedFinalRate = getFormattedCurrency(vehicle.finance.finalRate, locale, formatConfig);

  return (
    <div id="financing-calculator" className={styles.wrapper}>
      <Typography variant="h4" className={styles.title}>
        {strings.title}
      </Typography>
      <div className={styles.toggleWrapper}>
        <Toggle purchaseType="finance" selectedTab={selected} onClick={handleClick('finance')}>
          {strings.finance} {strings.from} {formattedMonthlyPrice} {strings.permonth}
        </Toggle>
        <Toggle purchaseType="purchase" selectedTab={selected} onClick={handleClick('purchase')}>
          {strings.buyFor} {formattedTotalPrice}
        </Toggle>
      </div>
      <div className={styles.tab}>
        {selected === 'finance' ? (
          <Tab
            benefits={[
              { label: strings.benefit1 },
              { label: strings.benefit4 },
              { label: strings.benefit2 },
              { label: strings.benefit5 },
              { label: strings.benefit3 },
              { label: strings.benefit6 },
            ]}
            strings={strings}
            key="finance"
          >
            <Typography variant="h5" className={styles.heading}>
              {strings.from} {formattedMonthlyPrice} {strings.permonth}
            </Typography>
            <Typography variant="body4">
              Anzahlung: {formattedDeposit}, Laufzeit: {vehicle.finance.months} Monate, Schlussrate:{' '}
              {formattedFinalRate} <br /> {strings.conditions}
            </Typography>
          </Tab>
        ) : (
          <Tab
            benefits={[
              { label: strings.benefit1 },
              { label: strings.benefit4 },
              { label: strings.benefit2 },
              { label: strings.benefit3 },
              { label: strings.benefit5, addition: strings.permitAddition },
              { label: strings.benefit6, addition: strings.deliveryAddition },
            ]}
            strings={strings}
            key="purchase"
          >
            <Typography variant="h5" className={styles.heading}>
              {formattedTotalPrice}
            </Typography>
            <Typography variant="body4">{strings.vatIncluded}</Typography>
          </Tab>
        )}
      </div>
    </div>
  );
};

function Toggle({
  purchaseType,
  children,
  onClick,
  selectedTab,
}: {
  purchaseType: PurchaseTypes;
  children: any;
  onClick: any;
  selectedTab: PurchaseTypes;
}) {
  return (
    <button
      className={cn(styles.button, { [styles.selected]: selectedTab === purchaseType })}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

function Tab({
  children,
  benefits,
  strings,
}: {
  children: any;
  benefits: { label: string; addition?: string }[];
  strings: InstamotionFinanceMessages;
  disclaimer?: string;
}) {
  return (
    <div className={styles.tab}>
      <div className={styles.tabHeader}>{children}</div>
      <ul>
        {benefits.map(({ label, addition }: any) => (
          <li>
            {addition ? <Minus fontSize={20} /> : <Tick fontSize={20} />}
            {label} <span>{addition ?? strings.included}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
